import type { CreateLicenceRequest } from '@shared/schemas';

import { validateCsm } from '../../_shared/domain/licence-csm';
import { validateLicenceIdentity } from '../../_shared/domain/licence-identity';
import type { LicenceDraft } from './licence-draft.model';

/** Field-level validation errors, keyed by draft field name. Empty = valid. */
export type ValidationErrors = Partial<Record<keyof LicenceDraft, string>>;

/** Inline message when the live availability check reports a duplicate. */
export const NAME_TAKEN_MESSAGE = 'A licence with this name already exists.';

/**
 * Pure business validation for a licence draft, mirroring the backend contract
 * (`CreateLicenceRequestSchema`): a non-empty name, a client category and a
 * country (#564): the category one of the options loaded, the country from the
 * shared list; and a Customer Success Manager (#695). No React, no Redux;
 * trivially unit-testable.
 */
export function validateDraft(draft: LicenceDraft): ValidationErrors {
  const errors: ValidationErrors = { ...validateLicenceIdentity(draft) };
  if (!draft.name.trim()) errors.name = 'A licence name is required.';
  const csm = validateCsm(draft.csm_user_id);
  if (csm) errors.csm_user_id = csm;
  return errors;
}

/**
 * The create request for a valid draft (trimmed name), or `null` when the draft
 * is not complete; the narrowing the saga needs to send the identity fields
 * without casting `''` away. The CSM (#583) is required too since #695.
 */
export function toCreateRequest(draft: LicenceDraft): CreateLicenceRequest | null {
  const name = draft.name.trim();
  if (!name || !draft.client_category || !draft.based_in || !draft.csm_user_id) return null;
  return {
    name,
    client_category: draft.client_category,
    based_in: draft.based_in,
    csm_user_id: draft.csm_user_id,
  };
}
